/**
 * Shared Zod validation helpers used across challan and annexure schemas.
 * Extracted to avoid duplication between legacy and new-form schema files.
 */
import { z } from 'zod'

/**
 * Optional numeric field: up to 12 digits and 2 decimal places.
 * Empty string defaults to "0".
 */
export const numeric12_2 = z
  .string()
  .regex(
    /^\d{0,12}(\.\d{0,2})?$/,
    'Must be a number up to 12 digits and 2 decimals'
  )
  .transform(val => (val === '' ? '0' : val))

/**
 * Mandatory numeric field: up to 12 digits and 2 decimal places.
 * Empty string defaults to "0".
 */
export const mandatoryNumeric12_2 = z
  .string()
  .nonempty('This field is required')
  .regex(
    /^\d{0,12}(\.\d{0,2})?$/,
    'Must be a number up to 12 digits and 2 decimals'
  )
  .transform(val => (val === '' ? '0' : val))

/**
 * Date in DD/MM/YYYY format with slash separator.
 */
export const ddMMYYYYWithSlash = z
  .string()
  .regex(
    /^(0[1-9]|[12][0-9]|3[01])\/(0[1-9]|1[0-2])\/\d{4}$/,
    'Invalid format. Example: 21/10/2024 (dd/mm/yyyy)'
  )

/**
 * Date in DD/MM/YYYY format that also allows an empty value.
 * Use for fields that are conditionally required (the required-ness is
 * enforced separately via a cross-field refinement).
 */
export const ddMMYYYYWithSlashOptional = z
  .string()
  .regex(
    /^$|^(0[1-9]|[12][0-9]|3[01])\/(0[1-9]|1[0-2])\/\d{4}$/,
    'Invalid format. Example: 21/10/2024 (dd/mm/yyyy)'
  )

/**
 * True when a numeric string field represents "no value": null, undefined,
 * empty, or zero (e.g. "0", "0.00"). mandatoryNumeric12_2 normalizes empty to
 * "0", so a saved row with no deposit arrives here as "0".
 */
export const isZeroOrEmptyNumeric = val =>
  val == null || String(val).trim() === '' || Number(val) === 0
